import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../../services/api';
import { useAuthV2 } from '../../../hooks/useAuthV2';

// STD-016: server state for the Maintenance domain. apiService is imported here
// (a *.queries.ts file) per STD-016. Errors are toasted by the global QueryCache
// handler (lib/queryClient); meta.errorMessage supplies the i18n fallback.
//
// These keys also replace a hand-rolled `window` CustomEvent bus that previously
// coupled the maintenance hooks: mutations invalidate the history/upcoming keys
// in onSuccess (maintenance.mutations.ts) and TanStack auto-refetches the queries
// below — no more `maintenance-record-*` window events.

export const maintenanceKeys = {
  all: ['maintenance'] as const,
  // Upcoming + history are both backed by per-drone record lists. They use
  // distinct endpoints, so they get distinct keys; mutations invalidate both.
  upcoming: (droneSerial?: string) => [...maintenanceKeys.all, 'upcoming', droneSerial] as const,
  history: (droneSerial?: string) => [...maintenanceKeys.all, 'history', droneSerial] as const,
  types: () => [...maintenanceKeys.all, 'types'] as const,
  // Maintenance-scoped org users (STD-016: do NOT import the AdminTools query —
  // a little cross-domain duplication is acceptable, cross-PR coupling is not).
  // Keyed by org id so a different org's list can't be served from cache after
  // an in-session org switch.
  organizationUsers: (orgId?: string) =>
    orgId
      ? ([...maintenanceKeys.all, 'organization-users', orgId] as const)
      : ([...maintenanceKeys.all, 'organization-users'] as const),
};

// Upcoming maintenance — all records for the drone, sorted newest-first. Fetched
// only when a drone is selected. Invalidated by every record mutation.
export function useUpcomingMaintenanceQuery(droneSerial?: string) {
  return useQuery({
    queryKey: maintenanceKeys.upcoming(droneSerial),
    queryFn: async () => {
      const records = await apiService.getDroneMaintenanceRecords(droneSerial!);
      // Sort by start_date descending (newest first) — preserves prior behaviour.
      return [...records].sort(
        (a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
      );
    },
    enabled: Boolean(droneSerial),
  });
}

// Maintenance history for a drone. Fetched only when a drone is selected.
// Invalidated by every record mutation.
export function useMaintenanceHistoryQuery(droneSerial?: string) {
  return useQuery({
    queryKey: maintenanceKeys.history(droneSerial),
    queryFn: () => apiService.getMaintenanceHistory(droneSerial!),
    enabled: Boolean(droneSerial),
  });
}

// Maintenance types — loaded when the create-record dialog opens. Passing
// enabled=false (dialog closed) avoids fetching until needed.
export function useMaintenanceTypesQuery(enabled = true) {
  return useQuery({
    queryKey: maintenanceKeys.types(),
    queryFn: () => apiService.getMaintenanceTypes(),
    enabled,
  });
}

// Organization users — loaded alongside the types when the dialog opens, used
// to populate the "performed by" selector.
export function useMaintenanceOrganizationUsersQuery(enabled = true) {
  const { profile } = useAuthV2();
  return useQuery({
    queryKey: maintenanceKeys.organizationUsers(profile?.organization?.id),
    queryFn: async () => {
      const data = await apiService.getOrganizationUsers();
      return data.users;
    },
    enabled,
  });
}

// Non-hook fetch of maintenance types — for non-React callers (e.g. the
// maintenance-type display-name cache in utils/maintenanceTypeUtils.ts) that
// cannot use the useMaintenanceTypesQuery hook. STD-016: apiService stays
// confined to this *.queries.ts file.
export function fetchMaintenanceTypes() {
  return apiService.getMaintenanceTypes();
}
